import {
    KentRecordType,
    KentRecordSubType
} from "./KentRecordTypes";
import { IStyleFactory } from "@esdm/map/dist/layers/vector/styles/IStyleFactory";
import Feature from "ol/Feature";
import Style, { StyleFunction } from "ol/style/Style";
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import RegularShape from 'ol/style/RegularShape';
import * as olHas from 'ol/has';

export class HbsmrStyleFactory implements IStyleFactory {

    public getStyle(recordType: string, recordSubType: string = null): (Style | Style[] | StyleFunction)
    {
        let style: (Style | Style[] | StyleFunction) = null;

        //try and resolve sub type first
        if (recordSubType !== null) {
            switch (recordSubType) {
                case KentRecordSubType.ConservationArea:
                    style = this.ConservationArea();
                    break;

                case KentRecordSubType.ScheduledMonument:
                    style = this.ScheduledMonument();
                    break;
            
                case KentRecordSubType.RegisteredParksAndGardens:
                    style = this.RegisteredParksAndGardens();
                    break;

                case KentRecordSubType.HistoricParksAndGardens:
                    style = this.HistoricParksAndGardens();
                    break;

                case KentRecordSubType.ListedBuilding:
                    style = this.ListedBuilding();
                    break;

                case KentRecordSubType.AngloSaxon:
                    style = this.AngloSaxon();
                    break;

                case KentRecordSubType.EarlyRoman:
                    style = this.Roman();
                    break;

                case KentRecordSubType.LateRoman:
                    style = this.Roman();
                    break;

                case KentRecordSubType.Harbour1500:
                    style = this.Harbour1500();
                    break;

                case KentRecordSubType.Medieval:
                    style = this.Medieval();
                    break;

                case KentRecordSubType.PostMedieval1638to1641:
                    style = this.PostMedieval1641();
                    break;

                case KentRecordSubType.PostMedieval1865:
                    style = this.PostMedieval();
                    break;

                case KentRecordSubType.PostMedieval1908:
                    style = this.PostMedieval();
                    break;


            }

        }

        if (style === null) {
            switch (recordType) {
                case KentRecordType.Monument:
                    style = this.MonumentStyle();
                    break;

                case KentRecordType.Designation:
                    style = this.Designation();
                    break;

                case KentRecordType.HistoricLandscapeCharacterisation:
                    style = this.HLCStyle();
                    break;
            }
        }

        return style;
    }
    
    public getSelectedStyle(recordType: KentRecordType): (Style | Style[] | StyleFunction) {
        let style: (Style | Style[] | StyleFunction) = null;

        switch (recordType) {
            case KentRecordType.Monument:
                style = this.MonumentSelectedStyle();
                break;

            case KentRecordType.Designation:
                style = this.DesignationSelected();
                break;
        }

        return style;
    }

    private CommonStyle(fillColour: string): Style[] {
        let fill = new Fill({
            color: fillColour
        });
        let stroke = new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 3
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: stroke,
                    radius: 12
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }
    
    private MonumentStyle(): StyleFunction {
        return function (feature: Feature) {
            let subtypecode = feature.get('recsubtypecode');
            let fillcolor = 'rgba(255,0,0,1)';
            let strokecolor = 'rgba(255,255,255,0.8)';
            // Monument – Circle - also used as default
            let iconChr: string = '\uf111';
            switch (subtypecode) {
                 //Listed Building – Landmark
                case 'LB':
                    iconChr = '\uf66f';
                    fillcolor = 'rgba(214,36,21,1)';
                    break;

                //Building – Building
                case 'BLD':
                    iconChr = '\uf1ad';
                    fillcolor = 'rgba(17,36,115,1)';
                    break;

                //Findspot – Star
                case 'FS':
                    iconChr = '\uf005';
                    fillcolor = 'rgba(45,115,9,1)';
                    break;

                //Farmstead – Triangle
                case 'FRM':
                    iconChr = '\uf2ec';
                    break;

                //Landscape – Trees
                case 'LND':
                    iconChr = '\uf724';
                    fillcolor = 'rgba(42,164,61,1)';
                    break;

                //Hedgerow – Tree-deciduous
                case 'HED':
                    iconChr = '\uf400';
                    fillcolor = 'rgba(153,205,100,1)';
                    break;

                //Maritime – Sailboat
                case 'MAR':
                    iconChr = '\ue445';
                    fillcolor = 'rgba(113, 78, 9, 1)';
                    break;

                //Place – Thumbtack
                case 'PLA':
                    iconChr = '\uf08d';
                    fillcolor = 'rgba(10, 10, 10, 1)';
                    break;

                //Important geological sequence – Shovel
                case 'GEO':
                    iconChr = '\uf713';
                    fillcolor = 'rgba(255, 102, 0, 1)';
                    break;

                //Pleistocene environmental findspot – skull-cow
                case 'PFS':
                    iconChr = '\uf8de';
                    fillcolor = 'rgba(255, 216, 0, 1)';
                    strokecolor = 'rgba(80,80,80,0.8)';
                    break;
            }
            
            let symbol = new Text({
                text: iconChr,
                font: '900 20px "Font Awesome 6 Pro"',
                textBaseline: 'bottom',
                offsetY: 11,
                fill: new Fill({
                    color: fillcolor
                }),
                stroke: new Stroke({
                    color: strokecolor,
                    width: 3
                })
            });
            return [new Style({ text: symbol})];
        }

    }

    private ListedBuilding(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\uf66f',
                            font: '900 20px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(178,0,255,1)' }),
                            stroke: new Stroke({
                                color: 'rgba(255,255,255,0.8)',
                                width: 3
                            })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({
                            color: 'rgba(178,0,255,0.4)'
                        }),
                        stroke: new Stroke({
                            color: 'rgba(178,0,255,1)',
                            width: 2
                        })
                    });
            }
        };
    }
    
    private Designation(): Style[] {
        return this.CommonStyle('rgba(132, 71, 15,0.4)');
    }

    private ConservationArea(): Style[] {
        //return this.CommonStyle('rgba(27, 15, 220, 0.4)');   
        let fill = new Fill({
            color: 'rgba(249, 122, 223, 0.4)'
        });
        let stroke = new Stroke({
            color: 'rgba(249, 122, 223, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private ScheduledMonument(): Style[] {       
        let fill = new Fill({
            color: 'rgba(32,77, 166, 0.4)'
        });
        let stroke = new Stroke({
            color: 'rgba(32,77, 166, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private RegisteredParksAndGardens(): Style[] {
        //return this.CommonStyle('rgba(45, 123, 9,0.5)');   
        let fill = new Fill({
            color: 'rgba(255, 170, 61, 0.4)'
        });

        let stroke = new Stroke({
            color: 'rgba(255, 170, 61, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private HistoricParksAndGardens(): Style[] {
        let fill = new Fill({
            color: 'rgba(45, 115, 9, 0.4)'
        });

        let stroke = new Stroke({
            color: 'rgba(45, 115, 9, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: fill,
                stroke: stroke,
                points: 5,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private HLCStyle(): StyleFunction {
        return function (feature: Feature) {
            let stroke = new Stroke({
                color: 'rgba(45, 45, 45, 1)',
                width: 1
            });

            let typecode: number = parseInt(feature.get('HLTYPE'));
            let color: string = '#fff';
            
            switch (typecode) {
                //1 - Field Patterns
                case 1:
                    color = 'rgba(209, 239, 143, 0.6)';
                    break;

                //2 - Commons
                case 2:
                    color = 'rgba(253, 173, 75, 0.6)';
                    break;

                //3 - Horticulture
                case 3:
                    color = 'rgba(255, 200, 199, 0.6)';
                    break;

                //4 - Woodland
                case 4:
                    color = 'rgba(118, 165, 74, 0.6)';
                    break;

                //5 - Reclaimed Marsh
                case 5:
                    color = 'rgba(51, 190, 183, 0.6)';
                    break;

                //6 - Downland
                case 6:
                    color = 'rgba(131, 145, 218, 0.6)';
                    break;

                //7 - Valley Floor and Water Management
                case 7:
                    color = 'rgba(166, 206, 227, 0.6)';
                    break;

                //8 - Coastal
                case 8:
                    color = 'rgba(255, 255, 115, 0.6)';
                    break;

                //9 - Settlements
                case 9:
                    color = 'rgba(153, 153, 153, 0.6)';
                    break;

                //10 - Parkland and Designed Landscape
                case 10:
                    color = 'rgba(128, 128, 51, 0.6)';
                    break;

                //11 - Recreation
                case 11:
                    color = 'rgba(252, 171, 220, 0.6)';
                    break;

                //12 - Extractive and Other Industry
                case 12:
                    color = 'rgba(217, 155, 98, 0.6)';
                    break;

                //13 - Inland Communication
                case 13:
                    color = 'rgba(185, 128, 214, 0.6)';
                    break;

                //14 - Military and Defence
                case 14:
                    color = 'rgba(255, 100, 100, 0.6)';
                    break;

            }
            
            let fill = new Fill({
                color: color
            });

            return [new Style({
                image: new RegularShape({
                    fill: fill,
                    stroke: stroke,
                    points: 5,
                    radius: 8,
                    radius2: 4,
                    angle: 0
                }),
                fill: fill,
                stroke: stroke
            })];
        }

    }
    
    private Selected(): Style[] {
        let fill = new Fill({
            color: 'rgba(63, 211, 0, 0.6)'
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            })
        ];
    }

    private DesignationSelected(): StyleFunction {
        return function (feature: Feature): Style {
            let subtypecode = feature.get('recsubtypecode');
            let fill = new Fill({
                color: 'rgba(63, 211, 0, 0.6)'
            });
            let style = new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            });

            switch (subtypecode) {
                case 'LB':
                    switch (feature.getGeometry().getType()) {
                        case 'Point':
                            return new Style({
                                text: new Text({
                                    text: '\uf66f',
                                    font: '900 20px "Font Awesome 6 Pro"',
                                    textBaseline: 'bottom',
                                    offsetY: 6,
                                    fill: new Fill({ color: 'rgba(76, 255, 0, 1)' }),
                                    stroke: new Stroke({
                                        color: 'rgba(0,0,0,0.8)',
                                        width: 5
                                    })
                                })
                            });
                        default:
                            return style;
                    }
                default:
                    return style;
            }
            
        }
    }

    private MonumentSelectedStyle(): StyleFunction {
        return function (feature: Feature) {
            let subtypecode = feature.get('recsubtypecode');
            // Monument – Circle - also used as default
            let iconChr: string = '\uf111';
            switch (subtypecode) {
                //Listed Building – Landmark
                case 'LB':
                    iconChr = '\uf66f';
                    break;

                //Building – Building
                case 'BLD':
                    iconChr = '\uf1ad';
                    break;

                //Findspot – Star
                case 'FS':
                    iconChr = '\uf005';
                    break;

                //Farmstead – Triangle
                case 'FRM':
                    iconChr = '\uf2ec';
                    break;

                //Landscape – Trees
                case 'LND':
                    iconChr = '\uf724';
                    break;

                //Hedgerow – Tree-deciduous
                case 'HED':
                    iconChr = '\uf400';
                    break;

                //Maritime – Sailboat
                case 'MAR':
                    iconChr = '\ue445';
                    break;

                //Place – Thumbtack
                case 'PLA':
                    iconChr = '\uf08d';
                    break;

                //Important geological sequence – Shovel
                case 'GEO':
                    iconChr = '\uf713';
                    break;

                //Pleistocene environmental findspot – skull-cow
                case 'PFS':
                    iconChr = '\uf8de';
                    break;
            }

            let symbol = new Text({
                text: iconChr,
                font: '900 20px "Font Awesome 6 Pro"',
                textBaseline: 'bottom',
                offsetY: 11,
                fill: new Fill({
                    color: 'rgba(255,255,0,1)'
                }),
                stroke: new Stroke({
                    color: 'rgba(0,0,0,0.8)',
                    width: 5
                })
            });
            return [new Style({ text: symbol })];
        }

    }

    //Dover UAD Styles
    AngloSaxon(): Style | Style[] | StyleFunction {
        return function (feature: Feature) {
            let stroke = new Stroke({
                color: 'rgba(45, 45, 45, 1)',
                width: 1
            });

            let typecode: string = feature.get('Type');
            let color: string = '#fff';

            switch (typecode) {
                case 'Cemetery':
                    color = 'rgba(116, 19, 235, 0.8)';
                    break;

                case 'Defences':
                    color = 'rgba(227, 26, 28, 0.8)';
                    break;

                case 'Haven':
                    color = 'rgba(91, 126, 206, 0.8)';
                    break;

                case 'Occupation':
                    color = 'rgba(231, 184, 75, 0.8)';
                    break;

                case 'Sands':
                    color = 'rgba(250, 214, 19, 0.8)';
                    break;

                case 'Shingle ridge':
                    color = 'rgba(210, 165, 117, 0.8)';
                    break;

                case 'Water meadows':
                    color = 'rgba(12, 254, 255, 0.8)';
                    break;
            }

            let fill = new Fill({
                color: color
            });

            return [new Style({
                image: new RegularShape({
                    fill: fill,
                    stroke: stroke,
                    points: 5,
                    radius: 8,
                    radius2: 4,
                    angle: 0
                }),
                fill: fill,
                stroke: stroke
            })];
        }
    }

    Harbour1500(): Style | Style[] | StyleFunction {
        return function (feature: Feature) {
            let stroke = new Stroke({
                color: 'rgba(45, 45, 45, 1)',
                width: 1
            });

            let typecode: string = feature.get('Type');
            let color: string = '#fff';

            switch (typecode) {
                case 'Defence':
                    color = 'rgba(227, 26, 28, 0.8)';
                    break;

                case 'Maritime':
                    color = 'rgba(2, 42, 248, 0.8)';
                    break;

                case 'Maritime/Harbour':
                    color = 'rgba(91, 126, 206, 0.8)';
                    break;

                case 'Religion and belief':
                    color = 'rgba(116, 19, 235, 0.8)';
                    break;
                
                case 'Undeveloped ground':
                    color = 'rgba(182, 189, 76, 0.8)';
                    break;
            }

            let fill = new Fill({
                color: color
            });

            return [new Style({
                image: new RegularShape({
                    fill: fill,
                    stroke: stroke,
                    points: 5,
                    radius: 8,
                    radius2: 4,
                    angle: 0
                }),
                fill: fill,
                stroke: stroke
            })];
            
        }
    }

    Medieval(): Style | Style[] | StyleFunction {
        return function (feature: Feature) {
            let typecode: string = feature.get('Type');
            let name: string = feature.get('Name');
            let color: string = '#fff';
            let stroke: Stroke = new Stroke({ color: color, width: 4 });

            switch (feature.getGeometry().getType()) {
                case 'LineString':
                    switch (typecode) {
                        case 'Defence':
                            color = 'rgba(227, 26, 28, 1)';
                            stroke = new Stroke({ color: color, width: 4 })
                            break;

                        case 'Maritime':
                            stroke = new Stroke({ color: 'rgba(2, 42, 248, 1)', width: 4, lineDash: [4, 6] })
                            break;
                    }

                    return [new Style({
                        stroke: stroke
                    })];
                    
                default:
                    stroke = new Stroke({
                        color: 'rgba(45, 45, 45, 1)',
                        width: 1
                    });

                    switch (typecode) {
                        case 'Defence':
                            color = 'rgba(227, 26, 28, 0.8)';
                            break;

                        case 'Domestic':
                            color = 'rgba(231, 184, 75, 0.8)';
                            break;

                        case 'Hospital':
                        case 'Hospitals':
                            color = 'rgba(198, 49, 235, 0.8)';
                            break;

                        case 'Industry':
                            color = 'rgba(125, 125, 125, 0.8)';
                            break;

                        case 'Maritime':
                            color = 'rgba(2, 42, 248, 0.8)';
                            break;

                        case 'Maritime/Harbour':
                            color = 'rgba(91, 126, 206, 0.8)';
                            break;

                        case 'Market':
                            color = 'rgba(242, 21, 117, 0.8)';
                            break

                        case 'Religion and belief':
                        case 'Religion and belief (Churchyard)':
                        case 'Religion and belief (Priory)':
                        case 'Religion and belief (priory)':
                            color = 'rgba(116, 19, 235, 0.8)';
                            break;

                        case 'River':
                            color = 'rgba(0, 112, 255, 0.8)';
                            break;

                        case 'Routeway':
                        case 'Routeways':
                            color = 'rgba(187, 125, 66, 0.8)';
                            break;

                        case 'Watermeadows':
                            color = 'rgba(12, 254, 255, 0.8)';
                            break;
                    }

                    let fill = new Fill({
                        color: color
                    });

                    return [new Style({
                        image: new RegularShape({
                            fill: fill,
                            stroke: stroke,
                            points: 5,
                            radius: 8,
                            radius2: 4,
                            angle: 0
                        }),
                        fill: fill,
                        stroke: stroke
                    })];
            }
        }
    }

    PostMedieval(): Style | Style[] | StyleFunction {
        return function (feature: Feature) {
            let stroke = new Stroke({
                color: 'rgba(45, 45, 45, 1)',
                width: 1
            });

            let typecode: string = feature.get('Type');
            let color: string = '#fff';

            switch (typecode) {
                case 'Agricultural and animal husbandry':
                    color = 'rgba(213, 147, 72, 0.8)';
                    break;

                case 'Civil':
                    color = 'rgba(228, 67, 148, 0.8)';
                    break;

                case 'Commercial':
                    color = 'rgba(43, 215, 49, 0.8)';
                    break;

                case 'Domestic':
                    color = 'rgba(231, 184, 75, 0.8)';
                    break;

                case 'Hospital':
                case 'Hospitals':
                    color = 'rgba(198, 49, 235, 0.8)';
                    break;

                case 'Hotel':
                case 'Hotels':
                    color = 'rgba(201, 61, 71, 0.8)';
                    break;

                case 'Industrial':
                    color = 'rgba(125, 125, 125, 0.8)';
                    break;

                case 'Leisure':
                    color = 'rgba(69, 176, 209, 0.8)';
                    break;

                case 'Maritime':
                    color = 'rgba(2, 42, 248, 0.8)';
                    break;

                case 'Maritime/Harbour':
                case 'Maritime/Harbours':
                    color = 'rgba(91, 126, 206, 0.8)';
                    break;

                case 'Military':
                    color = 'rgba(227, 26, 28, 0.8)';
                    break;

                case 'Parks and gardens':
                    color = 'rgba(167, 238, 45, 0.8)';
                    break;

                case 'Religion and belief':
                case 'Religion and belief (Churchyard)':
                case 'Religion and belief (Priory)':
                case 'Religion and belief (priory)':
                    color = 'rgba(116, 19, 235, 0.8)';
                    break;

                case 'River':
                    color = 'rgba(0, 112, 255, 0.8)';
                    break;

                case 'Routeways':
                    color = 'rgba(187, 125, 66, 0.8)';
                    break;

                case 'Schools':
                    color = 'rgba(0, 255, 197, 0.8)';
                    break;

                case 'Transport':
                    color = 'rgba(70, 20, 64, 0.8)';
                    break;

                case 'Utilities':
                    color = 'rgba(209, 139, 232, 0.8)';
                    break;
            }

            let fill = new Fill({
                color: color
            });

            return [new Style({
                image: new RegularShape({
                    fill: fill,
                    stroke: stroke,
                    points: 5,
                    radius: 8,
                    radius2: 4,
                    angle: 0
                }),
                fill: fill,
                stroke: stroke
            })];

        }
    }

    PostMedieval1641(): Style | Style[] | StyleFunction {
        return function (feature: Feature) {
            let stroke = new Stroke({
                color: 'rgba(45, 45, 45, 1)',
                width: 1
            });

            let typecode: string = feature.get('Type');
            let color: string = '#fff';

            switch (typecode) {
                case 'Bridge':
                    color = 'rgba(60, 60, 60, 0.8)';
                    break;

                case 'Civil':
                    color = 'rgba(228, 67, 148, 0.8)';
                    break;

                case 'Defence':
                    color = 'rgba(227, 26, 28, 0.8)';
                    break;

                case 'Developed Area':
                case 'Developed area':
                    color = 'rgba(231, 184, 75, 0.8)';
                    break;

                case 'Hospital':
                case 'Hospitals':
                    color = 'rgba(198, 49, 235, 0.8)';
                    break;

                case 'Industrial':
                case 'Industry':
                    color = 'rgba(125, 125, 125, 0.8)';
                    break;

                case 'Maritime':
                    color = 'rgba(2, 42, 248, 0.8)';
                    break;

                case 'Maritime/Harbours':
                    color = 'rgba(91, 126, 206, 0.8)';
                    break;

                case 'Parks gardens and leisure':
                    color = 'rgba(167, 238, 45, 0.8)';
                    break;

                case 'Religion and belief':
                case 'Religion and belief (Churchyard)':
                case 'Religion and belief (Priory)':
                    color = 'rgba(116, 19, 235, 0.8)';
                    break;

                case 'Routeways':
                    color = 'rgba(187, 125, 66, 0.8)';
                    break;

                case 'Shingle':
                    color = 'rgba(210, 165, 117, 0.8)';
                    break;

                case 'Trade and Commerce':
                    color = 'rgba(242, 21, 117, 0.8)';
                    break;
            }

            console.log(typecode, color);
            let fill = new Fill({
                color: color
            });

            return [new Style({
                image: new RegularShape({
                    fill: fill,
                    stroke: stroke,
                    points: 5,
                    radius: 8,
                    radius2: 4,
                    angle: 0
                }),
                fill: fill,
                stroke: stroke
            })];

        }
    }

    Roman(): Style | Style[] | StyleFunction {
        return function (feature: Feature) {
            switch (feature.getGeometry().getType()) {
                case 'LineString':
                    //Routeways
                    return new Style({
                        stroke: new Stroke({
                            color: 'rgba(187, 125, 66, 1)',
                            width: 4
                        })
                    });
                default:
                    let stroke = new Stroke({
                        color: 'rgba(45, 45, 45, 1)',
                        width: 1
                    });

                    let typecode: string = feature.get('Type');
                    let color: string = '#fff';

                    switch (typecode) {
                        case 'Building':
                            color = 'rgba(134, 130, 140, 0.8)';
                            break;

                        case 'Cemetery':
                            color = 'rgba(116, 19, 235, 0.8)';
                            break;

                        case 'Harbour':
                            color = 'rgba(91, 126, 206, 0.8)';
                            break;

                        case 'Lighthouse':
                            color = 'rgba(231, 17, 214, 0.8)';
                            break;

                        case 'Military':
                            color = 'rgba(227, 26, 28, 0.8)';
                            break;

                        case 'Peripheral':
                            color = 'rgba(182, 189, 76, 0.8)';
                            break;

                        case 'Settlement':
                            color = 'rgba(231, 184, 75, 0.8)';
                            break;
                    }

                    let fill = new Fill({
                        color: color
                    });

                    return [new Style({
                        image: new RegularShape({
                            fill: fill,
                            stroke: stroke,
                            points: 5,
                            radius: 8,
                            radius2: 4,
                            angle: 0
                        }),
                        fill: fill,
                        stroke: stroke
                    })];
            }
        }
    }
}
